<template>
  <section>
    <b-table :items="activityItems" :fields="FIELDS" table-class="table-minimal" show-empty>
      <template #cell(activity)="data">
        {{ getActivityLabel(data.item) }}
      </template>
      <template #cell(username)="data">
        {{ data.item.username || 'Stream' }}
      </template>
    </b-table>
  </section>
</template>

<script setup>
import moment from 'moment';
import { computed } from 'vue';
import { toFormattedDate } from '@/utils/date';

const ACTIVITY_KEYS = {
  FURLOUGHED: 'FURLOUGHED',
  GRANT_ASSIGNED: 'GRANT_ASSIGNED',
  GRANT_CLAIMED: 'GRANT_CLAIMED',
  OPTOUT_INITIATED: 'OPTOUT_INITIATED',
  OPTOUT_COMPLETED: 'OPTOUT_COMPLETED',
  PAUSED: 'paused',
  TRANSFERS: 'TRANSFERS',
  UNFURLOUGHED: 'UNFURLOUGHED',
  PROPSCHANGE: 'PROPSCHANGE',
  SALARY_CHANGE: 'SALARY_CHANGE',
};

const ACTIVITY_LABELS = {
  [ACTIVITY_KEYS.FURLOUGHED]: 'Furloughed',
  [ACTIVITY_KEYS.GRANT_ASSIGNED]: 'Grant Assigned',
  [ACTIVITY_KEYS.GRANT_CLAIMED]: 'Grant Claimed',
  [ACTIVITY_KEYS.OPTOUT_INITIATED]: 'Opt-out Initiated',
  [ACTIVITY_KEYS.OPTOUT_COMPLETED]: 'Opt-out Completed',
  [ACTIVITY_KEYS.PAUSED]: item => (item.properties.new.paused ? 'Paused' : 'Unpaused'),
  [ACTIVITY_KEYS.TRANSFERS]: item =>
    item.properties.new.transfers_disabled ? 'Pay Transfers Disabled' : 'Pay Transfers Enabled',
  [ACTIVITY_KEYS.UNFURLOUGHED]: 'Unfurloughed',
  [ACTIVITY_KEYS.PROPSCHANGE]: 'Employee Details Updated',
  [ACTIVITY_KEYS.SALARY_CHANGE]: 'Salary Change',
};

const ACTIVITY_TYPES = [
  ACTIVITY_KEYS.FURLOUGHED,
  ACTIVITY_KEYS.GRANT_ASSIGNED,
  ACTIVITY_KEYS.GRANT_CLAIMED,
  ACTIVITY_KEYS.TRANSFERS,
  ACTIVITY_KEYS.UNFURLOUGHED,
  ACTIVITY_KEYS.PROPSCHANGE,
  ACTIVITY_KEYS.SALARY_CHANGE,
];

const PROPERTY_BASED_ACTIVITIES = [ACTIVITY_KEYS.PAUSED];

const FIELDS = [
  {
    key: 'logged_at',
    label: 'Date/Time',
    sortable: true,
    formatter: value => toFormattedDate(value, 'YYYY-MM-DD HH:mm'),
  },
  {
    key: 'username',
    label: 'Performed by',
    sortable: true,
  },
  {
    key: 'activity',
    label: 'Action',
  },
];

const props = defineProps({
  activity: {
    type: Array,
    default: () => [],
  },
  optout: {
    type: Object,
    default: null,
  },
  salaryHistory: {
    type: Array,
    default: () => [],
  },
});

const activityItems = computed(() => {
  const activitySource = Array.isArray(props.activity) ? props.activity : [];

  const items = activitySource.filter(item => {
    return (
      ACTIVITY_TYPES.includes(item.activity) ||
      (item.properties?.new &&
        PROPERTY_BASED_ACTIVITIES.some(
          property => typeof item.properties.new === 'object' && property in item.properties.new,
        ) &&
        !(item.activity === ACTIVITY_KEYS.PROPSCHANGE && !item.username))
    );
  });

  items.push(
    ...props.salaryHistory
      .filter(item => item.new.salary && item.old.salary && item.new.salary != item.old.salary && item.username)
      .map(item => ({
        activity: ACTIVITY_KEYS.SALARY_CHANGE,
        logged_at: item.created_at,
        username: item.username,
        properties: { new: item.new, old: item.old },
      })),
  );

  // Add optout entries from the new endpoint
  if (props.optout?.initiated_at) {
    // Always show "Opt-out Initiated" entry
    items.push({
      activity: ACTIVITY_KEYS.OPTOUT_INITIATED,
      logged_at: props.optout.initiated_at,
      username: props.optout.username,
      properties: {},
    });

    // Show "Opt-out Completed" entry if completed
    if (props.optout.completed_at) {
      items.push({
        activity: ACTIVITY_KEYS.OPTOUT_COMPLETED,
        logged_at: props.optout.completed_at,
        username: props.optout.username,
        properties: {},
      });
    }
  }

  return items.sort((a, b) => moment(b.logged_at).diff(a.logged_at));
});

const getActivityLabel = item => {
  const key = getActivityKey(item);
  return typeof ACTIVITY_LABELS[key] === 'function' ? ACTIVITY_LABELS[key](item) : ACTIVITY_LABELS[key] || '';
};

const getActivityKey = item =>
  item.properties?.new
    ? Object.keys(item.properties.new).find(key => PROPERTY_BASED_ACTIVITIES.includes(key)) || item.activity
    : item.activity;
</script>
